import { useCallback, useState } from 'react';

import {
  QUOTE_STATUS,
  QUOTE_STATUS_LABEL,
  useSearchQuotes,
} from '@frontend/api';

import ExpandableFilterList from '../components/ExpandableFilterList';
import QuoteMatchCell from '../components/QuoteMatchCell';
import SearchResultList from '../components/SearchResultList';
import Searching from '../components/Searching';
import StatusFilter from '../components/StatusFilter';
import { UserGroupFilter } from '../components/UserGroupFilter';
import { TABS } from '../constants';
import { useSearchDataContext } from '../context';
import {
  buildFilterSeeMore,
  buildFilterUpdate,
} from '../utils/buildFilterUpdate';

const QUOTE_STATUSES = Object.values(QUOTE_STATUS).filter(
  (quoteStatus) => quoteStatus !== 'all',
);

const ALWAYS_VISIBLE_FIELDS = [
  'accountName',
  'contactEmail',
  'contactFullName',
  'number',
];

const QuoteResultList = () => {
  const { query, userHasTyped, activeTab } = useSearchDataContext();

  const [paginateFrom, setPaginateFrom] = useState(query.quotes.paginateFrom);

  const [filters, setFilters] = useState(query.quotes.filters);

  const { data: quotes } = useSearchQuotes(
    {
      ...query.quotes,
      paginateFrom,
      filters,
    },
    {
      queryOptions: {
        keepPreviousData: true,
        enabled: activeTab === TABS.QUOTES,
      },
    },
  );

  const onStatusChange = useCallback((status) => {
    setFilters((filters) => {
      const selectedStatuses = filters.status;
      return {
        ...filters,
        status: selectedStatuses.includes(status)
          ? selectedStatuses.filter((old) => old !== status)
          : [...selectedStatuses, status],
      };
    });
  }, []);

  const onAccountNameChange = useCallback(
    (accountName) =>
      buildFilterUpdate(
        setFilters,
        'accountNames',
        'accountNames',
      )(accountName),
    [],
  );

  const onAccountNameSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'accountNames')(),
    [],
  );

  if (userHasTyped) {
    return <Searching />;
  }

  return (
    <SearchResultList
      data={quotes}
      tabKey={TABS.QUOTES}
      removeMatchCellPadding
      labelPlural="Quotes"
      labelSingular="Quote"
      matchCell={(result) => <QuoteMatchCell quoteResult={result} />}
      highlightConfig={{
        alwaysVisibleFields: ALWAYS_VISIBLE_FIELDS,
      }}
      filters={
        <>
          <StatusFilter
            possibleStatuses={QUOTE_STATUSES}
            statusLabels={QUOTE_STATUS_LABEL}
            data={quotes}
            selectedStatuses={filters.status}
            onChange={onStatusChange}
          />
          <ExpandableFilterList
            agg={quotes?.aggs.accountNamesAgg.accountNames}
            label="Account Names"
            selectedKeys={filters.accountNames.accountNames}
            onClick={onAccountNameChange}
            onSeeMore={onAccountNameSeeMore}
          />
          <UserGroupFilter
            agg={quotes?.aggs.groupIdsAgg.groupIds}
            selectedGroupIds={filters.groupIds?.values ?? []}
            setFilters={setFilters}
          />
        </>
      }
      paginationInfo={{ paginateFrom, paginateSize: query.quotes.paginateSize }}
      onPaginate={(pagination) => setPaginateFrom(pagination.paginateFrom)}
    />
  );
};

export default QuoteResultList;
